/*
 * ACTIVE (direction §9). Phone-first; one thumb; the wanted datum is the strip.
 *
 * This is the perishable half of the product and the shadow log's permanent
 * replacement. Every other screen shows facts that can be re-read later; a strip
 * assignment is true for about four minutes and is then gone forever. That is why
 * §9 is the most specified screen in the design and why this file follows it
 * sentence by sentence rather than paraphrasing it.
 *
 * THE ANATOMY, top to bottom — three attached tiers, the same grammar Upcoming
 * and Past use (decisions 326, 327):
 *   eyebrow           "Active now", plus a tournament count when plural
 *   tournament card   white, bordered, one per tournament; a plain heading inside
 *                     it carrying dates and location. ALWAYS rendered — principle
 *                     9's plural-only band was reversed here, because a card whose
 *                     heading appears only alongside a second tournament has no
 *                     subject
 *   event sub-header  tinted and ATTACHED to the card below it, so the two read as
 *                     one object; round state + "as of" on line 2; a "View" pill
 *                     anchors the inline end
 *   fencer rows       ONE dense card per event with interior hairlines; name and
 *                     personal state leading, the strip at ~26px tabular trailing
 *                     under a small "Strip" caption
 *
 * WHAT IS DELIBERATELY ABSENT, so nobody adds it back by reflex:
 *   - No live dots, no "in progress" badges. Presence implies state (build rule 2):
 *     being in this view IS being live.
 *   - No countdown on the grace window (§5).
 *   - No denominators — "3rd", never "3rd of 40". Field size is not reliably owned
 *     data (contract §8), so it is unavailable rather than merely omitted. That
 *     applies to the pool position too: "3rd out of pools", never "3rd of 24", even
 *     though the server knows the field size (it computes the DE table from it).
 *   - No fencer-level link on a DE row: §9 withholds one until a constructible
 *     fencer-scoped destination exists, and there is none.
 *   - No estimated times, and never "bout ~" phrasing, which scans as a missing
 *     opponent.
 *
 *   - NO STALENESS TREATMENT AT ALL (decision 462, deviations #31). Nothing on
 *     this screen fades, shrinks, mutes or reformats with age — not the strip
 *     (454), and since 462 not a quiet event's header or its fencer rows
 *     either. Age reaches the reader in WORDS and nowhere else, and the words
 *     wait until 20 minutes, because "between rounds" genuinely lasts that long
 *     at a large tournament and amber at five minutes was crying wolf.
 *
 * WHAT ARRIVED SINCE (deviations #30): the opponent display §14 deferred — "vs
 * NAME" and "vs winner of X / Y" — with the pool position it is computed from.
 * §14's own trigger for it was "a decision to pull DE tableaus", and decisions
 * 456-458 are that decision. The wording is §9's; what §9 could not have is the
 * data, and now it does.
 */

import { Fragment, useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AsOf } from "../components/AsOf";
import { Band, EventSubHeader } from "../components/Band";
import { CapabilityPill } from "../components/CapabilityPill";
import { Card, CardRow } from "../components/Card";
import { HonestState } from "../components/HonestState";
import type { ActiveRail } from "../lib/activeRail";
import type { ActiveEvent, ActiveFencer, ActivePayload, ActiveTournament } from "../lib/api";
import {
  activeDays,
  beforeStart,
  coverageOf,
  coverageTier,
  emptyReason,
  lastCheckedIn,
  lastObservedIn,
  deStatusOf,
  needsDayMarkers,
  poolRecordValues,
  resumesLater,
  roundStateOf,
  tournamentSpansMonths,
  visibleGroups,
  whenKeyFor,
  withholdRoundState,
} from "../lib/activeState";
import { formatLocation } from "../lib/country";
import {
  activeTime,
  dateLocale,
  dateRange,
  deviceZone,
  firstLast,
  readerDayName,
  type ActiveTimeParts,
} from "../lib/format";
import { isTeamRow, teamNames } from "../lib/team";
import { TeamLine } from "../components/TeamLine";
import { DEFAULT_GRACE_POLICY, outcomeLabel, type GracePolicy } from "../lib/grace";
import { elapsedKey, minutesSince, stalenessTier } from "../lib/staleness";
import styles from "./Active.module.css";
// The container grammar shared with Upcoming and Past. Two classes only —
// everything Active-specific stays in its own module.
import grouped from "./Grouped.module.css";

export interface ActiveProps {
  payload: ActivePayload;
  /** §14's future setting. Always the default today; a parameter so it can arrive. */
  policy?: GracePolicy;
  /** Injectable for tests and screenshots. */
  now?: Date;
  locale?: string;
  /**
   * The reader's zone: the day marker's "Today" is its day, and a venue far from
   * it gets the reader's clock beside its times (decisions 787, 788). Injectable
   * for tests, as `now` is; defaults to the browser's.
   */
  device?: string;
  /**
   * Which rail to draw beside a card of fencers (roadmap item 4). PROTOTYPE
   * SCAFFOLDING and an ADDITION to the standard layout, not a replacement for
   * it — `none` is the default and is what every ordinary visit renders. See
   * `lib/activeRail.ts`.
   */
  rail?: ActiveRail;
  /**
   * The list-scope control, or null (multi-list build item 3).
   *
   * A SLOT, so this screen keeps the property the container exists to give it:
   * pure over its props, testable and screenshottable without a router. The
   * control is gated on two conditions the route holds (decision 510) and reads
   * the router to close its panel, and neither belongs here.
   */
  listScope?: ReactNode;
  /**
   * How many live events the caller has ACROSS ALL LISTS, passed only when this
   * view's scope is narrower than all of them and the number is not zero
   * (decision 503).
   *
   * Two of the three conditions for the sentence below are therefore already
   * checked by the time this arrives; the third is `emptyReason`, which is this
   * screen's own reading of its payload. Undefined means the sentence does not
   * apply, and the existing empty copy stands.
   */
  hiddenLiveEventCount?: number;
}

export function Active({
  payload,
  policy = DEFAULT_GRACE_POLICY,
  now = new Date(),
  locale = dateLocale(),
  device = deviceZone(),
  rail = "none",
  listScope = null,
  hiddenLiveEventCount,
}: ActiveProps) {
  const groups = visibleGroups(payload, policy, now);

  if (groups.length === 0) {
    return (
      <ActiveEmpty
        reason={emptyReason(payload)}
        listScope={listScope}
        {...(hiddenLiveEventCount === undefined ? {} : { hiddenLiveEventCount })}
      />
    );
  }

  /*
   * THE RAIL IS ONE CLASS ON THE TOURNAMENT CARD and nothing else — the whole
   * of Active's layout is now declared on `.screen` in this screen's own
   * stylesheet, so there is no variant plumbing in EventGroup or FencerRow and
   * no class at all on the standard rendering.
   *
   * Empty for `none`, so an ordinary visit carries the exact class list it
   * would if this prototype did not exist.
   */
  const railClass =
    rail === "a" ? grouped.railNeutral : rail === "b" ? grouped.railTinted : "";

  return (
    <div className={styles.screen}>
      <ActiveHead listScope={listScope} />

      {/*
        THE THREE TIERS, adopted from Upcoming and Past (decisions 326, 327): a
        white bordered card per tournament, a tinted event header attached to the
        card of fencers beneath it, interior hairlines doing every division. The
        structural classes come from Grouped.module.css rather than being copied
        here, so there is ONE definition of the container and its descendant rules
        — `.tournamentCard .eventBlock > *` has to match this screen's header and
        card, and a second copy would drift from the original the first time
        either was touched.

        THE TOURNAMENT HEADING NOW ALWAYS RENDERS, which reverses principle 9's
        plural-only rule for this screen. It is not chrome any more: it is the
        label on the outer tier, and a bordered card whose heading appears only
        when a second tournament exists is a card with no subject.
      */}
      {groups.map(({ tournament, events }) => {
        /*
          DAY SEPARATORS ON A MULTI-DAY TOURNAMENT, and only there (build rule
          3's plural chrome). Active states times and never days, which is right
          within one day: "9:00 AM CDT" is the whole answer when there is only
          one day it could be. Across a weekend it is not — a band reading
          "Fri 4 – Sun 6 Sep" over a list whose first event is Saturday morning
          reads as "this one is Friday", which is how it was reported from the
          deployed app.

          The events were already in day order; what was missing was the mark
          that makes the order legible. See `activeDays` and `needsDayMarkers`,
          which is where the rule about WHICH tournaments get them lives.
        */
        const days = activeDays(events);
        const markDays = needsDayMarkers(tournament);
        // THE TOURNAMENT'S OWN FTL PAGE, offered only when an event on screen
        // has no page of its own — FTL listing it under a name the matcher
        // could not pair (FAD, 2026-10-04) or not at all. Where every event
        // links, the tournament page would duplicate them, which is why the
        // band had no pill before.
        const tournamentHref = events.some(({ event }) => !event.event_url)
          ? tournament.tournament_url
          : undefined;
        // Build rule 7 on the day marker: the month only where it is what
        // separates two of these. See DaySeparator.
        const spansMonths = tournamentSpansMonths(tournament);

        return (
          <section
            key={tournament.tournament_id}
            className={[grouped.tournamentCard, railClass].filter(Boolean).join(" ")}
          >
            <TournamentBand tournament={tournament} locale={locale} href={tournamentHref} />

            {days.map((day) => (
              <Fragment key={day.date ?? "undated"}>
                {markDays ? (
                  <DaySeparator
                    date={day.date}
                    locale={locale}
                    spansMonths={spansMonths}
                    now={now}
                    device={device}
                  />
                ) : null}

                {day.events.map(({ event, fencers }) => (
                  <EventGroup
                    key={event.event_id}
                    event={event}
                    fencers={fencers}
                    timezone={tournament.timezone ?? null}
                    now={now}
                    locale={locale}
                    device={device}
                    tournamentLinked={tournamentHref != null}
                  />
                ))}
              </Fragment>
            ))}
          </section>
        );
      })}
    </div>
  );
}

/**
 * The tournament heading: the outer tier's label, inside its white card.
 *
 * `plain` — so no tint, no border, no padding of its own. Decision 326 moved the
 * emphasis DOWN a level in the grouped views, because a tinted tournament above
 * bare event text read as "new topic" and detached events from the tournament
 * they belonged to. Active had kept the boxed band on the grounds that its middle
 * tier carries round state rather than a quiet label; that argument survives — the
 * event header is still the tinted one — but the OUTER tier needs the same
 * treatment for the same reason, and the container now supplies the extent a box
 * used to.
 *
 * Headers are never links (§9, build rule 5). Active's link-outs are per event
 * ("View") and per fencer ("Pool N"), and the band carries a View pill of its
 * own ONLY when an event on screen has no page: then the tournament's FTL page
 * is the one place left to look (Kevin, post-weekend item 7 — FAD's event was
 * on FTL under a different name). Where every event links, a tournament-level
 * destination would duplicate them without adding a capability, so it stays
 * off.
 */
function TournamentBand({
  tournament,
  locale,
  href,
}: {
  tournament: ActiveTournament;
  locale: string;
  href?: string | undefined;
}) {
  // City and country abroad, city and region at home (decision 776).
  const where = formatLocation(tournament.city, tournament.region, tournament.country, locale);
  const when = dateRange(tournament.start_date, tournament.end_date, locale);

  return (
    <Band
      plain
      name={tournament.name}
      meta={
        // One dot-separated line (decision 363): when and where are one fact,
        // wrapping naturally when the band is narrow.
        <>
          {when ? <span className="tc-num">{when}</span> : null}
          {when && where ? " · " : null}
          {where || null}
        </>
      }
      action={href ? <CapabilityPill kind="view" href={href} /> : undefined}
    />
  );
}

/**
 * The day heading between event groups.
 *
 * A HEADING AND NOT A STYLED DIV, at h3 — between the tournament's h2 and each
 * event's h3... which is the one thing worth explaining. The event sub-headers
 * are h3 already, so a day marker above them wants to be h3 too rather than
 * pushing every event to h4: the day is a SIBLING divider inside the card, not a
 * level of nesting the events live under. Screen-reader users get it in reading
 * order either way, and heading-level nesting that reshuffles on a data
 * condition is worse than a flat pair.
 *
 * QUIET, because it is a divider rather than a fact anyone chases: the strip and
 * the time are what a coach is scanning for, and a day marker that competed with
 * them would be chrome shouting over data (§6).
 *
 * VENUE-LOCAL BY CONSTRUCTION, with nothing to convert. `event_date` is a SQL
 * DATE, sent as "2026-09-05" with no zone, so it formats in UTC — which is not
 * a fudge but the only reading that returns the day the organiser typed (the
 * trap format.ts documents at length).
 *
 * IT SAYS "TODAY" AND "TOMORROW", reversing deviations #27's own rule (Kevin,
 * 2026-09-10). That entry declined them because a relative word GOES STALE — a
 * phone left open overnight would show Sunday's events under "Tomorrow" on
 * Sunday morning — and concluded that a date cannot be wrong. What it was
 * weighing against was thin, and that is what changed: the tournament band
 * directly above already states the span, so the marker repeating "Sat 5 Sep"
 * spends the screen's narrowest line telling the reader something they read
 * two lines ago. "Today" is the one thing the band cannot say.
 *
 * The staleness objection is answered rather than dismissed. `now` is threaded
 * from the same clock every other relative label on this screen uses, so this
 * cannot drift independently of them — if "as of 4 min ago" is current, so is
 * this — and the wake-up refresh (decision 384) re-asks on `visibilitychange`,
 * which is precisely the pocketed-phone case #27 was worried about. Anything
 * further out than tomorrow still falls back to the date, which cannot be
 * wrong.
 *
 * NULL RENDERS NOTHING AT ALL. An event no source has dated gets its own group
 * and no heading rather than being filed under the day above it (contract §0).
 *
 * THE MONTH APPEARS ONLY ACROSS A MONTH SPAN, which is build rule 7's own rule
 * and the same correction Upcoming's event dates just took. The band directly
 * above this marker already says "Thu 10 – Fri 11 Sep", so "Thu 10 Sep" on the
 * marker restates a month the reader has just read — on the screen with the
 * least width in the app, and on a tier whose whole job is to be a quiet
 * divider. "Thu 10" is the divider; the month belongs to the tier that owns the
 * span.
 *
 * DECIDED FROM THE TOURNAMENT'S SPAN, not the marker's own date, for the reason
 * deviation #27 already gives for the marker existing at all: the question is
 * what disambiguates WITHIN the group being read, and the group is the
 * tournament. A Nov 30 – Dec 1 tournament keeps its months on both markers,
 * because there the month is the thing that separates them.
 */
function DaySeparator({
  date,
  locale,
  spansMonths,
  now,
  device,
}: {
  date: string | null;
  locale: string;
  spansMonths: boolean;
  now: Date;
  device: string;
}) {
  const { t } = useTranslation();
  // The READER's day, not the venue's (decision 787): Upcoming names a day by
  // the venue's; Active is read by someone watching along, whose "Tomorrow" is
  // their own. Only the label moves -- which day a card belongs to stays the
  // venue's (activeState).
  const named = readerDayName(date, locale, spansMonths, now, device);
  if (!named) return null;
  const label =
    named.kind === "today"
      ? t("date.today")
      : named.kind === "tomorrow"
        ? t("date.tomorrow")
        : named.label;
  return <h3 className={`${styles.daySeparator} tc-num`}>{label}</h3>;
}

/**
 * A time sentence and, where the venue is far from the reader, the reader's own
 * clock after it (decision 788): "Starts at 8:00 AM GMT+1 (Fri 11:00 PM for
 * you)".
 *
 * TWO SPANS, EACH NOWRAP, joined by a plain space, so a narrow screen can break
 * the line only before the parenthesis and never inside the zone label or the
 * weekday. The same arrangement Upcoming's split-event line uses.
 */
function TimeWithReader({
  parts,
  labelKey,
}: {
  parts: ActiveTimeParts;
  labelKey: "active.round.poolsAt" | "active.round.startsAt" | "active.when.onStripAt" | "active.when.atTime";
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className={styles.timeMain}>{t(labelKey, { time: parts.venue })}</span>
      {parts.reader ? (
        <>
          {" "}
          <span className={styles.timeReader}>{t("active.readerTime", { time: parts.reader })}</span>
        </>
      ) : null}
    </>
  );
}

/**
 * The quiet state's label, assembled from two localized fragments.
 *
 * A helper rather than inline JSX because HonestState's label is a STRING: its
 * whole contract is that the label is always visible and the detail is disclosed,
 * and letting callers pass arbitrary nodes into the label is how that turns into
 * a second nested disclosure.
 */
function quietLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  roundKey: string,
  lastSeen: string | null,
  now: Date,
): string {
  // toLowerCase because the round state is a sentence-leading phrase elsewhere
  // ("Fencing pools") and here it is mid-sentence ("Was fencing pools"). Locale
  // aware via the browser's own casing rules; languages without case are
  // unaffected, which is why this is safe to do unconditionally.
  const what = t(roundKey).toLocaleLowerCase();
  const stopped = t("staleness.updatesStopped", { what });
  if (!lastSeen) return stopped;

  const { key, count } = elapsedKey(minutesSince(lastSeen, now));
  return `${stopped} · ${t("staleness.lastUpdate", { ago: t(key, { count }) })}`;
}

function EventGroup({
  event,
  fencers,
  timezone,
  now,
  locale,
  device,
  tournamentLinked = false,
}: {
  event: ActiveEvent;
  fencers: ActiveFencer[];
  timezone: string | null;
  now: Date;
  locale: string;
  device: string;
  /** The band above carries the tournament's FTL page (see TournamentBand). */
  tournamentLinked?: boolean;
}) {
  const { t } = useTranslation();

  const coverage = coverageOf(event);
  const round = roundStateOf(event, now, timezone);
  // Between a two-day event's days (decision 784): the next day's start is
  // stated as the scheduled start is before anything has begun.
  const resuming = resumesLater(event, now, timezone);
  const notYetDue = beforeStart(event, now);
  const tier = coverageTier(event, now);
  const quiet = tier === "quiet";
  const lastSeen = lastObservedIn(event);
  // The "as of" is when we last LOOKED (migration 0055), which is newer than
  // lastSeen whenever the poller read an unchanged status. The quiet line keeps
  // lastSeen: it is about the source going silent, not about our reading.
  const lastChecked = lastCheckedIn(event);

  /*
   * A POOL TIME BELONGS TO THE EVENT WHEN EVERY POOL SHARES IT.
   *
   * A round that runs all its pools at once has one start, so printing it on each
   * fencer's row is one fact repeated N times on the screen with the least room to
   * spare. A FLIGHTED round genuinely assigns each pool its own, and then the
   * per-fencer time is the whole point.
   *
   * `pools_share_one_start` is the server's, computed over ALL the round's pools,
   * and it has to be: the client is sent only the tracked fencers' pools, so two
   * fencers in one flight look exactly like a round starting all at once — and
   * their flight's time would be promoted as though it covered the event.
   *
   * The flag is also FALSE where any pool's time is unpublished, which is not the
   * same fact as a flight but calls for the same rendering: there is no one time
   * that can be stated for the round, so the times stay on the rows that own them.
   *
   * DE rows are untouched — a DE bout time is per fencer by nature, never a
   * flight — which is what `pool_number` distinguishes below.
   */
  /*
   * CHECK-IN STOPS BEING THE USEFUL FACT ONCE A ROUND IS UNDERWAY (item 1,
   * weekend enhancements). "Checked in" answers "is she here yet", and that
   * stops mattering the moment there is a strip or an outcome to report
   * instead — but those are FENCER-level facts (the ladder in FencerRow) and
   * this is an EVENT-level override for the case neither of those covers
   * yet: a DE round, where check-in was never the useful fact to begin with,
   * or a round/event that has already moved on while this particular fencer
   * has no strip or outcome of her own (a bye, a late add). `preparing` does
   * NOT suppress — pools assigned or a bracket posted still means the
   * fencer's arrival is the open question. Mirrors `hidePoolTime` above: an
   * event-level fact threaded down to gate one row-level rendering.
   */
  const showCheckIn =
    event.round_kind !== "de" &&
    event.round_status !== "fencing" &&
    event.round_status !== "complete" &&
    event.event_status !== "complete";

  const poolRound = event.round_kind === "pool";
  const sharedPoolStart =
    poolRound && event.pools_share_one_start
      ? // ANY pool row's time will do, and that is a consequence of the flag
        // rather than an approximation: the server sets it true only when every
        // pool in the round carries the SAME published instant, so the first row
        // that has one is authoritative. `find` rather than a reduce for that
        // reason.
        (fencers.find((f) => f.pool_number != null && f.starts_at != null)?.starts_at ?? null)
      : null;

  /*
   * THE TIME COMES OFF THE HEADER ONCE THE POOLS ARE UNDER WAY (Kevin, from the
   * 12 Sep event): "Pools at 9:00 AM" is a plan, and a plan stops being worth a
   * line the moment it has been overtaken by what is actually happening. After
   * that the strip is the answer to every question the time was answering, and
   * the header's room is better spent on the round state and the "as of".
   *
   * STARTED IS TWO CONDITIONS, and the pair is Kevin's: the pools are POSTED and
   * the start time HAS PASSED. Posted is already implied here -- `sharedPoolStart`
   * is read off a fencer row that carries both a pool number and a time, so a
   * round with no assignments published cannot produce one -- so the only test
   * left to make is the clock's. Stated as both anyway, because the implication
   * is a property of how the value above is derived and not of the rule.
   *
   * A clock comparison and not a round state, deliberately. "Fencing pools" can
   * appear before a single bout is called, and the published start is the thing
   * the reader was told; once it is in the past, the sentence is stale whatever
   * the round says about itself.
   *
   * `poolsPosted` IS WHAT THE ROWS READ, not what the header is showing. The two
   * were one value until this change: had `hidePoolTime` stayed keyed on
   * `poolTimeOnHeader != null`, every row would have sprouted its own copy of the
   * time at exactly the moment the header stopped saying it — the opposite of
   * what was asked. A shared start is said ONCE or not at all, never per row.
   */
  const poolsPosted = sharedPoolStart != null;
  const poolsUnderway = poolsPosted && new Date(sharedPoolStart).getTime() <= now.getTime();
  const poolTimeOnHeader = poolsUnderway
    ? null
    : activeTime(sharedPoolStart, { timeZone: timezone, locale, device });

  /*
   * THE SCHEDULED START, shown only while nothing has started (decision 355).
   *
   * A not-started event has no round, no pools and no observed times -- and it
   * is exactly the state a coach opens the app in to plan the morning. The
   * canonical schedule's start is the one time that exists, so it renders on
   * the event line. The moment a round opens, observed state outranks it: a
   * schedule is a plan, and showing the plan next to what actually happened
   * invites reading the wrong one.
   *
   * A TWO-DAY EVENT BETWEEN ITS DAYS is the same situation one day on
   * (decision 784): its next round is drawn and nothing in it has begun, and
   * the server's start is that day's. Casablanca's table of 64 reads "Bracket
   * posted · Starts at 9:00 AM" under Saturday.
   */
  const scheduledStart =
    (event.round_kind == null || resuming) && coverage !== "uncovered" && event.event_status !== "complete"
      ? activeTime(event.event_starts_at, { timeZone: timezone, locale, device })
      : null;

  /*
   * THE FLIGHTED MARKER (decision 355): when the round does NOT share one
   * start and a visible row actually carries its own time, the event says so
   * -- "make sure the user doesn't miss it" is the requirement's own wording.
   * Without the marker, a reader who glances at the event line, sees no time,
   * and assumes none was published never looks down at the rows.
   *
   * Only with a row-level time in evidence, deliberately: the flag is also
   * false where some pool's time is simply unpublished, and announcing
   * "flighted" over an absence would be a claim the data does not make.
   */
  const flighted =
    poolRound &&
    !event.pools_share_one_start &&
    fencers.some((f) => f.pool_number != null && f.starts_at != null);

  /*
   * WENT QUIET IS A DIFFERENT RENDERING, NOT A DIMMED COPY — and getting this
   * wrong was a real bug caught by the first screenshot pass.
   *
   * The obvious implementation is `opacity` on the whole <section>, which the
   * screen inventory's shorthand ("container opacity") invites. It is
   * unimplementable: opacity establishes a stacking context and a child CANNOT
   * exceed its parent's value, so `opacity: 1` on the explanatory line does
   * nothing. The result dimmed the words — violating §6's "dim the datum, never
   * the words" — and dimmed the disclosure BUTTON with them, which made a working
   * control look disabled.
   *
   * §9's prose says what to do instead: "muted header, 'Was fencing pools —
   * updates stopped · last update N min ago', last-known fencer state in fine
   * print". Muted is a COLOUR on the header; fine print is a smaller, compact row
   * for each fencer. The strip stops being the hero datum because it is no longer
   * current — which is the honest thing for the layout itself to say — and every
   * word and control stays at full contrast.
   */
  /*
   * THE ROUND LINE'S PIECES, in order, skipping whatever does not apply.
   *
   * The first is the round-state label, and it is the one that can be absent
   * (decision 463): "Between rounds" is the only label in the stack that claims
   * something is COMING, and it is reached by inference from a completed round
   * with nothing after it -- so when every fencer here already has an outcome,
   * the line says nothing about the round and the outcomes speak for
   * themselves. See withholdRoundState.
   */
  const roundMeta: ReactNode[] = [];
  if (!withholdRoundState(round, fencers)) {
    roundMeta.push(
      /*
        THE LABEL, and for one state it comes with a disclosure.
        `active.round.unknown` means FTL reported a state we have no word for,
        and §9 keeps the raw string "for the disclosure to quote — never the
        label". Until the demo seed put an unmapped status on screen there was
        nowhere for it to be quoted: only the quiet branch built an HonestState,
        so "Round state unclear" was a dead end and the source's own words were
        dropped.

        That is the wrong state to lose them in. It is the one case where the app
        is admitting it does not understand the source, so the source's words are
        the only thing that can help — and decision 152 records the archive's
        status vocabulary as incomplete, which makes this reachable on any real
        weekend rather than theoretical.

        Neutral rather than amber: amber is §6's aging signal, and an
        unrecognised state is not a stale one.
      */
      round.key === "active.round.unknown" ? (
        <HonestState
          label={t(round.key)}
          detail={
            round.raw
              ? t("active.unknownDetailWithRaw", { raw: round.raw })
              : t("active.unknownDetail")
          }
        />
      ) : flighted ? (
        /*
          THE FLIGHT QUALIFIES THE ROUND RATHER THAN FOLLOWING IT (amending
          decision 355 and deviation #23). The line used to carry two elements
          — "Fencing pools" and then, further along, "Flighted — each fencer
          has their own time" — which is sixteen words of round state on the
          screen with the least width in the app, and they were saying one
          thing: these pools do not start together.

          So one phrase, "Fencing pools (flighted)", built by qualifying
          whatever the pool round's own label is. That keeps "Pools assigned
          (flighted)" working for a `preparing` round rather than special-casing
          the one state.

          AND 355'S SENTENCE IS RETIRED, NOT RELOCATED (Kevin, 2026-09-10). An
          intermediate version kept it behind a disclosure, on 355's own
          reasoning — "make sure the user doesn't miss it", because a reader who
          glances at the line, sees no time and assumes none was published never
          looks down at the rows. That does not survive looking at the result:
          every row underneath already carries its own time, visibly and
          differently from its neighbours, so the sentence explained something
          the screen had already shown. A disclosure over an obvious fact is
          still chrome, and putting it one tap away does not make it less so.

          A BARE STRING, therefore, exactly like every other round label.
        */
        t("active.round.flightedLabel", { label: t(round.key) })
      ) : (
        t(round.key)
      ),
    );
  }
  // Pools finished of total, when FTL published BOTH. Not a denominator of the
  // kind contract §8 bars — that rule is about field size, which nobody owns;
  // this pair is published data.
  if (round.pools) {
    roundMeta.push(
      <span className="tc-num">
        {t("active.round.poolsProgress", {
          complete: round.pools.complete,
          total: round.pools.total,
        })}
      </span>,
    );
  }
  // The unflighted event's one pool time, stated once here instead of once per
  // fencer row. Phrased "Pools at 9:00 AM" rather than a bare time, because a
  // bare number on the round line would be ambiguous against the "as of".
  if (poolTimeOnHeader) {
    roundMeta.push(
      <span className="tc-num">
        <TimeWithReader parts={poolTimeOnHeader} labelKey="active.round.poolsAt" />
      </span>,
    );
  }
  // The scheduled start, while nothing has begun (decision 355). "Starts at",
  // not "Pools at": this is the canonical schedule's claim, not an observation.
  if (scheduledStart) {
    roundMeta.push(
      <span className="tc-num">
        <TimeWithReader parts={scheduledStart} labelKey="active.round.startsAt" />
      </span>,
    );
  }
  // NO SEPARATE FLIGHTED ELEMENT any more: it is folded into the round-state
  // label above, so the line carries one phrase where it used to carry two.
  // See the flighted branch there for what happened to 355's sentence.
  /*
   * THE "AS OF" IS NOT IN THIS LIST, and that is the whole of fix 5.
   *
   * It used to be the last element of `roundMeta`, which made the meta one
   * inline run that wrapped as a lump: a narrow phone broke it wherever the
   * text happened to reach the edge, routinely mid-phrase, so "as of 4 min
   * ago" came apart across two lines. That phrase is the one thing on the
   * header a coach reads to decide whether to trust the strip below it.
   *
   * So it became its own element beside the list rather than an item in it.
   * Since Kevin's review after the 2026 October NAC it is not beside the list
   * at all: it is the header's `below` row, a line of its own under the name
   * and the round state, because beside the round state it read as a stray
   * fragment in a different size and colour.
   *
   * DECISION 463'S PROPERTY IS UNTOUCHED: `roundMeta` is still a LIST joined
   * with middots, and a missing first element is still expressible. It is
   * strictly easier now, since the element most likely to be present has left
   * the list. The middot between the status and the "as of" goes with it: a
   * middot separates items in one run, and these two never share a line.
   */

  /*
   * THE SECTION IS THE EVENT BLOCK, and that is a correctness requirement rather
   * than tidiness.
   *
   * The container strips interior edges with `.tournamentCard .eventBlock > *`,
   * which reaches DIRECT children only. While this section sat inside a separate
   * wrapper div, the header and the card were grandchildren — so the rule zeroed
   * the radius on the wrapper, which has none, and the `attached` variants kept
   * theirs: a rounded top on every event header and a rounded bottom on every
   * last fencer row, curving away from the straight container edge beside them.
   *
   * `attached` squares only the corners facing its own neighbour — the header's
   * bottom, the card's top — because outside a container those two are the only
   * ones that meet. The remaining four are the container's to flatten, and it can
   * only do that if these are its grandchildren's parents' children, so to speak:
   * one element per event block, holding both.
   */
  return (
    <section className={grouped.eventBlock}>
      <EventSubHeader
        // ATTACHED: this header and the card below it are one object, with the
        // header's block-end border dropped so the seam between them is a single
        // hairline (decision 326).
        attached
        name={event.event_name}
        // h3 by default, under the tournament's h2. No longer conditional, since
        // the tournament heading always renders and the level never changes.
        //
        // NOT `muted` when the event has gone quiet (decision 462): an event
        // nobody has heard from in half an hour reads exactly like a live one,
        // and its AGE is stated in the words below. See the treatment note in
        // this file's header.
        meta={
          quiet ? (
            /*
             * §9's quiet line: "Was fencing pools — updates stopped · last update
             * N min ago". Past tense, because the claim is about what we last knew
             * rather than what is true now.
             *
             * Built as an HonestState because that is precisely what it is — a
             * succinct always-visible label with the explanation behind a
             * disclosure — and it is where FTL's raw status string earns its place
             * in the payload: quoted in the detail, never used as the label.
             */
            <span className={styles.quietLine}>
              <HonestState
                tone="amber"
                label={quietLabel(t, round.key, lastSeen, now)}
                detail={
                  round.raw
                    ? t("active.quietDetailWithRaw", { raw: round.raw })
                    : t("active.quietDetail")
                }
              />
            </span>
          ) : coverage === "uncovered" ? (
            /*
             * NOTHING, and `undefined` rather than an empty fragment because
             * EventSubHeader tests `meta` for truthiness and a fragment would
             * still get the styled row.
             *
             * §9's state stack gives no-coverage-ever exactly ONE sentence —
             * "Not covered on FTL — check the venue board" — and the paragraph
             * below is where it lives, with the wording the spec uses. A round
             * label here said the same thing a second time in different words
             * ("No FencingTime Live page"), which is the densest screen in the
             * app spending two lines on the state that has the least to say.
             *
             * It is also the more honest structure: this slot holds ROUND state,
             * and an uncovered event has none to hold — the same reasoning
             * coverageTier already uses to give it no staleness tier.
             */
            undefined
          ) : roundMeta.length > 0 ? (
            /*
             * THE META LINE, assembled as a LIST and joined with middots rather
             * than each piece carrying its own separator.
             *
             * It was the second shape until decision 463 made the first element
             * optional: with hardcoded separators, withholding the round label
             * left the line starting " · 4 of 4 pools done", and the fix for
             * that is not a conditional separator on every piece.
             */
            <span className={styles.roundStatus}>
              {roundMeta.map((node, i) => (
                <Fragment key={i}>
                  {i > 0 ? " · " : null}
                  {node}
                </Fragment>
              ))}
            </span>
          ) : undefined
        }
        /*
         * THE "AS OF" ON A LINE OF ITS OWN, beneath the name and the round state
         * (Kevin, after the 2026 October NAC): beside the round state it read as a
         * stray fragment in a different size and colour. Rendered only WITH an
         * observation, and not on the quiet line, which states its own age, or
         * on an uncovered event, which has none.
         *
         * NOR BETWEEN A TWO-DAY EVENT'S DAYS (784; Kevin, 2026-10-16): the line
         * says when the next day starts, and FTL's blank status leaves the last
         * look on the evening before, so "as of 7 hours ago" describes nothing a
         * reader can act on. And BEFORE ANY EVENT'S START it never goes amber:
         * nothing is owed yet, so an old reading is not a stale one. The age is
         * still stated.
         */
        below={
          !quiet && !resuming && coverage !== "uncovered" && lastChecked ? (
            <span className={styles.roundAsOf}>
              <AsOf observedAt={lastChecked} regime="day-of" now={now} mayAge={!notYetDue} />
            </span>
          ) : undefined
        }
        action={
          /*
           * THE CAPABILITY RULE. The View pill renders strictly from the presence
           * of its destination, so an uncovered event has no pill because there is
           * no page — not because a flag said to hide it (§9, contract §0).
           */
          event.event_url ? (
            <CapabilityPill kind="view" href={event.event_url} />
          ) : null
        }
      />

      {/*
        ONE CARD FOR THE WHOLE FENCER LIST, with interior hairlines — the same
        shape Upcoming and Past use, and a reversal of §9's card-per-fencer rule
        for this screen.

        §9 argued a card each because an Active row carries a strip and a personal
        state where Upcoming's carries only a name, so "card-per-name would be
        border noise" did not apply. What that missed is the screen's own budget:
        Active is the view that should be shrinking all day as fencers finish, and
        eight bordered boxes with gaps between them spend the vertical space that
        the next event's strip needs. The hairlines still separate the rows; what
        goes is the border and the gap around each one.

        `dense` for the same reason, and it reverses the note on Card's own prop —
        which reasoned that Active rows are read at arm's length and earn the
        roomier padding. True of the DATUM, which is 26px and unchanged; the
        padding around it was buying height rather than legibility.

        The uncovered paragraph stays OUTSIDE the card: it describes the event, not
        a fencer, and putting it inside would make it read as a row.
      */}
      {/*
        TWO SENTENCES, BECAUSE WE CANNOT TELL THE TWO CASES APART. An event with
        no FTL page may be missing from FTL, or listed there under a name the
        matcher could not pair (FAD's "D & Under (Div 3) Senior Mixed Epee" was
        FTL's "Div III Mixed Epee"). With the tournament's page linked above,
        "Not covered on FencingTime Live" would contradict the link, so the
        sentence points at it instead.
      */}
      {coverage === "uncovered" ? (
        <p className={styles.noCoverage}>
          {tournamentLinked ? t("active.noCoverageDetailTournament") : t("active.noCoverageDetail")}
        </p>
      ) : null}

      {/*
        ONE RENDERING, WHATEVER THE AGE (decision 462, retiring §9's fine
        print). A quiet event's fencers used to demote to one line each -- name,
        a text-sized strip, a time -- inside a plain block instead of a card.
        That was the last of the staleness TREATMENTS, and it went for decision
        454's reason: a strip is read across a sports hall in a hurry, and the
        rendering that said "trust this less" was the same rendering that made
        it harder to read. The age still reaches the reader, in words, on the
        line above.
      */}
      {/*
        FLUSH: the card gives up its border, radius and fill, so the seam
        between an event header and its first row is a change of colour rather
        than a drawn line and the block reads as one object with a tinted label
        on top. It also widens the header's tint step, because the header is
        then measured against the ground rather than against the card —
        1.08:1 becomes 1.14:1 in light and 1.09:1 becomes 1.18:1 in dark, which
        is finding C fixed as a side effect.

        Everything else about the row — the hairlines, the dense padding, the
        trailing answer column — is unchanged, which is why this is a prop on
        Card rather than the rows being lifted out of one. Lifting them out is
        the honest end state and is a follow-up: it would mean moving
        `.cardDense .row`'s padding somewhere a screen stylesheet can reach.
      */}
      <Card attached dense flush>
        {fencers.map((f) => (
          <FencerRow
            // A team's card is keyed by its team (decision 714): one card per
            // team, and two registered teams can share no alias.
            key={f.team_id ?? f.alias_id}
            fencer={f}
            timezone={timezone}
            hidePoolTime={poolsPosted}
            showCheckIn={showCheckIn}
            notYetDue={notYetDue}
            now={now}
            locale={locale}
            device={device}
          />
        ))}
      </Card>
    </section>
  );
}

/*
 * The row no longer takes `coverage`. It needed it for one thing only -- the
 * "Registered" line under an uncovered event's fencers -- and decision 450
 * removed that. Coverage is an EVENT-level fact and the event line states it;
 * threading it down to the row was what made the restatement possible.
 */
function FencerRow({
  fencer,
  timezone,
  hidePoolTime,
  showCheckIn,
  notYetDue,
  now,
  locale,
  device,
}: {
  fencer: ActiveFencer;
  timezone: string | null;
  device: string;
  /**
   * True when the event header is carrying the pool time, so this row must not
   * repeat it. Set only for an unflighted pool round — see EventGroup.
   */
  hidePoolTime: boolean;
  /**
   * False once check-in stops being the useful fact for this event — a DE
   * round, or a round/event that has moved past its start — so the trailing
   * slot's check-in rung must not render. Set at the event level; see
   * EventGroup.
   */
  showCheckIn: boolean;
  /**
   * True before the event's start -- or its next day's, for a two-day event
   * between days (decision 784). Nothing is owed yet, so the row does not say
   * how old its strip is. See `beforeStart`.
   */
  notYetDue: boolean;
  now: Date;
  locale: string;
}) {
  const { t } = useTranslation();

  const done = outcomeLabel(fencer);
  const de = deStatusOf(fencer);
  // A team's card names its tracked members first and the team second
  // (decision 714); see lib/team.ts.
  const team = isTeamRow(fencer) ? teamNames(fencer.team_members) : null;
  // The P marker's revealed sentence: one per row at a time, and the row is
  // where both lines it can appear under live.
  const [priorityNote, setPriorityNote] = useState<PriorityNote | null>(null);
  const priority: PriorityNoteState = {
    note: priorityNote,
    setNote: setPriorityNote,
    ids: { name: useId(), matchup: useId() },
  };
  // The tier decides whether the age is STATED, and nothing else since decision
  // 454: no datum on this row changes its rendering with age.
  const aging = !notYetDue && stalenessTier(fencer.as_of, "day-of", now) !== "fresh";

  /*
   * A POOL row's time is suppressed where the header states it; a DE row's never
   * is. `pool_number` is what tells them apart — pass 3 of the payload assembly
   * clears it when a DE assignment supersedes a pool one, so a row carrying a
   * pool number is a pool row by construction.
   *
   * Suppressing the TIME rather than the whole ladder matters: "On strip" still
   * renders, because a strip with no time means go now and that is a different
   * statement from the time the header carries.
   */
  /*
   * WHETHER THIS FENCER'S OWN POOL HAS FINISHED (migration 0028), which ends
   * the field ladder below as surely as an outcome does. Guarded on
   * `pool_number` so it can only ever describe a POOL row: a DE assignment
   * clears the pool fields on the way past (see the API's pass 3), but reading
   * the flag without the guard would be one refactor away from suppressing a
   * DE bout's time because of a pool round that ended an hour ago.
   */
  const poolDone = fencer.pool_number != null && fencer.pool_complete_as_of != null;
  // The pool record's values, or null (post-Remenyik item 4).
  const poolRecord = poolRecordValues(fencer.pool_record);

  /*
   * HAS SHE ADVANCED PAST HER FIRST DE? — the decay condition for the pool
   * position (amending decision 457, which kept the position for as long as
   * the fencer was competing).
   *
   * "9th out of pools" is how she got INTO the bracket, and it stops meaning
   * anything the moment she has won a bout: from then on where she stands is
   * the table she is in, and the pool position is a fact about a round two
   * rounds ago competing for the tallest line on the screen. Build rule 2 —
   * drop the line when it stops meaning something rather than restating it.
   *
   * MEASURED AGAINST THE FIRST BOUT'S TABLE, NOT THE FIRST TABLE SHE STOOD IN,
   * which is the whole subtlety and is why the server sends the number rather
   * than the client deriving it. A fencer who byes the table of 32 arrives in
   * the 16 having fenced nobody, so a naive "her table is smaller than the one
   * the draw put her in" would decay the line on an advance she did not earn.
   * `first_bout_table_size` already halves for a bye, so the comparison below
   * is against the table of her first actual bout.
   *
   * Both sides are needed: the observed table only exists once the bracket has
   * spoken, and `first_bout_table_size` is the only thing that survives the
   * projection being withheld at that same moment (decision 458). Absent
   * either, nothing decays — which is the conservative direction, since the
   * position is a true fact and the cost of keeping it is a line.
   */
  const advancedPastFirstDE =
    fencer.table_size != null &&
    fencer.first_bout_table_size != null &&
    fencer.table_size < fencer.first_bout_table_size;

  const suppressTime = hidePoolTime && fencer.pool_number != null;
  const when = whenKeyFor(fencer, { withoutTime: suppressTime });
  const time = suppressTime
    ? null
    : activeTime(fencer.starts_at, { timeZone: timezone, locale, device });

  return (
    <>
      <CardRow
        className={styles.fencerRow}
        trailing={
          <>
            {/*
              The pool pill, when a pool sheet destination exists. Labelled with
              its DESTINATION — "Pool 5", never a generic "Details" (§9) — so two
              pills in the same view never share a word while meaning different
              places.
            */}
            {fencer.pool_number != null && fencer.pool_url ? (
              <CapabilityPill
                kind="pool"
                poolNumber={fencer.pool_number}
                href={fencer.pool_url}
              />
            ) : null}

            {/*
              THE SLOT. §5: the outcome replaces the strip in the SAME slot, so a
              fencer finishing does not move the layout under a thumb. Exactly
              one of these renders, ever.

              CHECK-IN IS THE THIRD RUNG, and it is here rather than in the body
              because this column is where the reader already looks: "Strip 4",
              "Finished 12th" and "Checked in" are three answers to one question
              — where is my fencer — and splitting them across two places makes
              the eye hunt for the answer it did not get. Kevin's rule, from the
              pilot feedback, and it is the same argument §5 makes for the
              outcome replacing the strip rather than sitting beside it.

              THE ORDER IS SPECIFICITY, not recency. An outcome is terminal. A
              strip says exactly where to stand, which is strictly more than
              "they are here". "Checked in" fills the slot only while neither
              exists — which is the morning, and is the state the pilot users
              were asking about.

              A published TIME does not suppress it, unlike the other rungs: the
              time renders in the body, so the two do not compete for this space,
              and "Checked in · 4:06 PM" is two facts a coach wants together.
            */}
            {done ? (
              <span className={styles.outcome}>
                {done.kind === "placed" ? (
                  <>
                    <span className="tc-num">
                      {t(done.key, { count: done.place, ordinal: true })}
                    </span>
                    {/* §11's tie rendering: a localized ordinal plus a
                        DE-EMPHASIZED "(tie)" suffix. Never "3T" — USA Fencing's
                        T-notation does not localize and misreads beside an
                        ordinal. */}
                    {done.tie ? (
                      <span className={styles.tie}>{t("active.outcome.tie")}</span>
                    ) : null}
                  </>
                ) : done.kind === "outIn" ? (
                  /*
                    THE SECOND DONE SIGNAL (decision 459), and the earlier one:
                    "Out in the table of 32" while the placement is still
                    settling below the bracket. It says WHERE, which
                    "Eliminated" cannot, and it is stable where the placement's
                    denominator is the number decision 442 watched drift ("21st
                    of 23", then of 31, then of 39).

                    The placement replaces it in this same slot the moment it
                    lands — outcomeLabel prefers it — so the row states the
                    more informative fact at every moment and never both.
                  */
                  <span className="tc-num">{t(done.key, { table: done.table })}</span>
                ) : done.kind === "outInUnclear" ? (
                  // A bracket heading we have no word for: §9 bars the raw
                  // string as a label, so the slot says she is out and the
                  // disclosure carries FTL's own words.
                  <HonestState
                    label={t(done.key)}
                    detail={t("active.outcome.outInUnclearDetail", { raw: done.raw })}
                  />
                ) : (
                  t(done.key)
                )}
              </span>
            ) : fencer.strip ? (
              <Strip value={fencer.strip} />
            ) : fencer.status === "checked_in" && showCheckIn ? (
              // Quieter than an outcome and quieter than a strip: it is
              // reassurance, not an exception, and §9 reserves the loud
              // treatments for things that have gone wrong.
              <span className={styles.checkedIn}>{t("active.checkedIn")}</span>
            ) : null}
          </>
        }
      >
        {/* First Last for display (decision 358, as amended). Every card is a
            resolved fencer's since the name claim went (decision 813), so no
            row quotes its source and every name goes through firstLast. */}
        <div className={styles.name}>
          {team && team.tracked.length > 0
            ? team.tracked.join(t("team.nameSeparator"))
            : firstLast(fencer.name)}
          {/* The tracked fencer's own P, only while a DE matchup is on the
              card -- it is a fact about that bout, not about the fencer. */}
          {!done && de?.opponent?.pSelf ? (
            <PriorityMark
              name={firstLast(fencer.name)}
              at="name"
              state={priority}
            />
          ) : null}
        </div>
        <PriorityNoteLine state={priority} at="name" />


        {/*
          THE TEAM, in a team event (decisions 696, 714). Said because it changes
          what the strip means: FTL follows the team, so the strip on this card
          is the team's, and the name is what the coach finds on the bracket. The
          name alone, with no "Team" label: the event heading already says it is
          a team event (Kevin, 2026-10-10). Then the rest of the roster, the
          people nobody here tracks (Kevin, 2026-10-12).
        */}
        {fencer.team_name ? (
          <TeamLine teamName={fencer.team_name} members={fencer.team_members} className={styles.sub} />
        ) : null}

        {/*
          NO CLUB LINE (decision 362): the reader already knows the club of a
          fencer they chose to track, so on the temporal views it was a line of
          space spent restating it. The wire still carries `club` — identity
          surfaces (Admin's queue, the roster, the search) keep rendering it,
          because there a club is what disambiguates a spelling.
        */}

        {/*
          THIS FENCER'S OWN POOL HAS FINISHED (migration 0028), which is the
          twenty minutes the screen previously had nothing to say about.

          THE LADDER, and each rung supersedes the one above it: while the pool
          is fencing there is no line at all; once her pool is done the line
          says so; once the ROUND closes the position replaces it, because
          "3rd out of pools" is strictly more than "your pool finished". That
          is presence-implies-state (build rule 2) rather than three variants of
          one badge — the absence of this line while the pool runs is the
          honest answer and not a gap.

          SUPPRESSED BY A POSITION AND BY AN OUTCOME. Both say more, and both
          arrive later, so this never renders beside either.

          NO DISCLOSURE (Kevin, on the first screenshot). The obvious misreading
          is "the round is over", and the first draft answered it with an
          HonestState explaining that the other pools are still fencing — but
          the EVENT LINE two rows up already says "1 of 3 pools done", which is
          that same fact in the place that owns it. A disclosure repeating it
          would be one fact stated twice, and putting the copy behind a
          collapsed control does not make it not a duplicate.
        */}
        {/*
          THE POOL RECORD (post-Remenyik item 4) REPLACES THE BARE LINE when one
          exists: "Pool complete: 4-1  +11". It does NOT give way to the position
          -- the record and "3rd out of pools" say different things, and it
          stays until DEs start, when the server stops sending it. The two spaces
          are Kevin's and are literal in the catalogue; `white-space: pre-wrap`
          on the line is what keeps them (a collapsed pair would read "4-1 +11",
          the indicator running on from the losses), while still wrapping on a
          narrow phone.
        */}
        {!done && poolRecord ? (
          <div className={`${styles.poolPlace} ${styles.poolRecord} tc-num`}>
            {t("active.poolRecord", poolRecord)}
          </div>
        ) : !done && fencer.pool_place == null && fencer.pool_complete_as_of ? (
          <div className={styles.poolPlace}>{t("active.poolComplete")}</div>
        ) : null}

        {/*
          THE POOL POSITION, once the round is over (decision 456), AND ONLY
          UNTIL SHE HAS WON A DE (amending decision 457).

          Only while the fencer is still competing: an outcome in the slot means
          "Finished 21st", and how they came in pools is then history the reader
          did not ask for. Absent all through the round, which is the honest
          answer rather than a gap — FTL publishes a running ordering and it is
          not a position until the organiser closes the round.

          AND IT DECAYS. 457 kept this line for as long as the fencer was
          competing; it stops being worth its space the moment she has won a
          bout, because from then on where she stands is the TABLE she is in and
          this is a fact about a round two rounds ago. Build rule 2: drop the
          line when it stops meaning anything rather than restating it. See
          `advancedPastFirstDE` for why the comparison is against the first
          BOUT's table rather than the first table she stood in — the bye is the
          case that distinction exists for.

          NO DENOMINATOR (contract §8): "3rd out of pools". The server knows the
          field size — it computes the DE table from it — and deliberately does
          not send it, because a placement's denominator is the value decision
          442 records drifting.
        */}
        {!done && !advancedPastFirstDE && fencer.pool_place != null ? (
          <div className={styles.poolPlace}>
            <span className="tc-num">
              {t("active.poolPlace", { count: fencer.pool_place, ordinal: true })}
            </span>
            {fencer.pool_place_tie ? (
              <span className={styles.tie}>{t("active.outcome.tie")}</span>
            ) : null}
          </div>
        ) : !done && fencer.exempt_from_pools ? (
          // EXEMPT FROM POOLS (migration 0057): the seeding's own flag, in the
          // slot her pool place would hold. She has none, and without this the
          // card said nothing at all until the DE named her.
          <div className={styles.poolPlace}>{t("active.exemptFromPools")}</div>
        ) : null}

        {/*
          WHAT THE FORMAT SAYS IS NEXT (migration 0057), in the matchup's slot
          and INSTEAD of it: below the cut there is no first bout, inside the
          next DE's exemptions there is none in that DE, and through a DE
          fenced "until N remain" the bout the bracket still shows is one she
          has won. The server sends at most one of these and withholds the
          projection beside each, so this is the second of two gates.
        */}
        {!done && fencer.next_round ? (
          <div className={styles.opponent}>
            {t(fencer.next_round.exempt ? "active.de.straightToTable" : "active.de.throughToTable", {
              table: fencer.next_round.table_size,
            })}
          </div>
        ) : !done && fencer.likely_eliminated ? (
          <div className={styles.opponent}>{t("active.likelyEliminated")}</div>
        ) : null}

        {/*
          WHERE SHE IS IN THE DE, AND WHO IS ACROSS FROM HER (decisions 457,
          458) — the display §14 deferred until there was a bracket to pull it
          from.

          THE HONESTY IS IN THE VERB. "vs SMITH Jo" is what the bracket or the
          draw says, and both are facts; "vs winner of SMITH Jo / DOE Alex" is
          what the draw says when the fencer has a bye and the bout that
          decides her opponent has not been fenced. Neither predicts anything,
          and neither is hedged — which is the pair of mistakes available here.
          §9's own wording, used verbatim.

          THE TABLE STANDS ON ITS OWN. It is the whole status line for a fencer
          the strips queue has drained past — decision 127 makes publishing no
          strips at all the ordinary case — and at the moment the projection is
          most useful the DE round does not exist yet, so the event header above
          says "Between rounds" and nothing else states which table the bout is
          in. Where the heading is one we have no word for, §9's rule applies as
          it does to the round state: our vocabulary as the label, the source
          quoted in a disclosure, never the raw string as the label.

          Suppressed by an outcome for the same reason the pool position is, and
          this one is stronger: a fencer who is out has no next bout at all. The
          server withholds the projection too (decision 246's rule), so this is
          the second of two gates rather than the only one.
        */}
        {!done && de && !fencer.next_round ? (
          <div className={styles.opponent}>
            {de.table ? (
              <>
                <span className="tc-num">{t(de.table.key, { count: de.table.count })}</span>
                {/* THE BYE SITS WITH THE TABLE, not after the sentence
                    (Kevin, on the first screenshot pass): "Table of 16 (bye) ·
                    vs winner of …". At the end it landed after a line that had
                    wrapped, which is where a two-word qualifier goes unread —
                    and it qualifies the TABLE rather than the opponent. She
                    does not fence this one; the sentence after the middot is
                    about the bout that follows. */}
                {de.opponent?.bye ? (
                  <span className={styles.byeNote}>{t("active.de.byeNote")}</span>
                ) : null}
                {de.opponent ? " · " : null}
              </>
            ) : de.tableRaw ? (
              <>
                <HonestState
                  label={t("active.de.tableUnclear")}
                  detail={t("active.de.tableUnclearDetail", { raw: de.tableRaw })}
                />
                {de.opponent ? " · " : null}
              </>
            ) : null}
            {/* The sentence in its own element, so it is one string to read and
                one string to query: the table beside it is a separate fact and
                the middot between them is not part of either. */}
            {de.opponent ? (
              <span>
                {de.opponent.kind === "vs" || de.opponent.kind === "orBye"
                  ? // One name either way; "orBye" adds the bye as the other
                    // possibility (her tied place straddles the bye line).
                    withNodes(
                      t(de.opponent.kind === "orBye" ? "active.de.vsOrBye" : "active.de.vs", {
                        name: marker("name"),
                      }),
                      {
                        name: (
                          <Named
                            name={firstLast(de.opponent.a)}
                            priority={de.opponent.pA}
                            state={priority}
                          />
                        ),
                      },
                    )
                  : de.opponent.kind === "winnerOf" || de.opponent.kind === "eitherOf"
                    ? // Two names either way; the sentence differs (decision
                      // 690): the winner of a bout, or one of two tied fencers.
                      withNodes(
                        t(
                          de.opponent.kind === "eitherOf" ? "active.de.vsEitherOf" : "active.de.vsWinnerOf",
                          { a: marker("a"), b: marker("b") },
                        ),
                        {
                          a: (
                            <Named
                              name={firstLast(de.opponent.a)}
                              priority={de.opponent.pA}
                              state={priority}
                            />
                          ),
                          b: (
                            <Named
                              name={firstLast(de.opponent.b)}
                              priority={de.opponent.pB}
                              state={priority}
                            />
                          ),
                        },
                      )
                    : t("active.de.bye")}
              </span>
            ) : null}

          </div>
        ) : null}
        {!done && de && !fencer.next_round ? <PriorityNoteLine state={priority} at="matchup" /> : null}

        {/*
          §9's field ladder, and it only applies while the fencer is still
          competing: once an outcome is in the slot, "On strip at 2:40" is a
          statement about a bout that has been fenced.

          A FINISHED POOL ENDS IT TOO (Kevin, on the first screenshot). The
          ladder carries TENSE — "On strip at 1:58 PM" says go and stand there —
          and once this fencer's own pool is complete that is a sentence about
          bouts already fenced, exactly as an outcome makes it. The time is not
          merely redundant by then, it is wrong, and it sat directly above the
          line saying the pool was done.

          The whole ladder goes rather than just the time, because the tense is
          the part that stops being true: "On strip" over a finished pool is no
          better than "On strip at 1:58 PM". That is the opposite of decision
          329's suppression, which drops the TIME and keeps the ladder precisely
          because "go now" is still true there.
        */}
        {!done && !poolDone && when && when !== "onStrip" && time ? (
          <div className={styles.when}>
            <TimeWithReader
              parts={time}
              labelKey={when === "onStripAt" ? "active.when.onStripAt" : "active.when.atTime"}
            />
          </div>
        ) : null}
        {!done && !poolDone && when === "onStrip" ? (
          <div className={styles.when}>{t("active.when.onStrip")}</div>
        ) : null}

        {/*
          §9's aging row label, stated plainly with a disclosure carrying the
          explanation. AMBER rather than red: red is interaction, and an aging
          reading is information (§6). The words never dim — only the strip does.

          IT NO LONGER SAYS "may have fenced" (decision 446). The string used to
          read "strip seen 14 min ago — may have fenced", and on a DE that is a
          prediction we have no data to make: the strips payload drains as bouts
          finish, but nothing here knows the rate, so an aged reading is equally
          consistent with a bout fenced twenty minutes ago and one about to
          start. Pilot users at the 5-7 Sep weekend read it as a statement about
          their fencer. What is left is the observation itself — when we last
          read the posting — which is true on a DE row and on a pool row alike,
          so there is no round-kind branch here.

          THE ROW STAYS rather than being deleted with the claim, and decision
          454 made it load-bearing rather than merely paired: the strip's
          opacity WAS the other half of §6's two channels, and now that the fade
          is gone these words are the only place the age is stated at all.
          Removing them would leave a fifteen-minute-old strip indistinguishable
          from one read this second, which is the failure §6 was guarding
          against from the other side.

          THE `fencer.strip` GUARD IS LOAD-BEARING, and its absence was a real
          bug the demo seed surfaced. `as_of` comes from a DE round assignment's
          `observed_at`, which is written whether or not the organiser published
          a strip — and decision 127 records posting none as the ORDINARY case,
          where the bracket table is the line. So a fencer with a published bout
          time and no strip claimed a strip reading that never existed. No datum,
          no staleness claim: the same rule as Strip's null-renders-nothing.
        */}
        {!done && aging && fencer.as_of && fencer.strip ? (
          <HonestState
            tone="amber"
            label={t("staleness.stripSeen", {
              ago: t(elapsedKey(minutesSince(fencer.as_of, now)).key, {
                count: elapsedKey(minutesSince(fencer.as_of, now)).count,
              }),
            })}
            detail={t("staleness.stripSeenDetail")}
          />
        ) : null}

        {/*
          NO "REGISTERED" LINE (decision 450, reversing §9's "fencers shown as
          registered" for the uncovered event).

          An uncovered event used to print "Registered" under every fencer's
          name. Being on the screen at all IS being registered — the list is
          built from registrations — so the word restated the one thing the row
          could not fail to mean, on the state that already has the most text on
          it. Presence implies state (build rule 2), which is the same rule that
          keeps Upcoming from badging its own 95% case.

          What the reader actually needs here is on the EVENT line, where it was
          already: "Not covered on FencingTime Live — check the venue board."
          That says why there is no strip. The per-fencer line never did.
        */}
      </CardRow>
    </>
  );
}

/**
 * The strip, at ~26px tabular in the trailing slot — the datum the whole screen
 * exists to deliver.
 *
 * STALENESS NO LONGER TOUCHES IT (decision 454, amending 314 and 446, against
 * §6's "staleness is opacity" and §9's day-of calibration). The strip used to
 * fade through three opacity steps and drop from semibold to regular past five
 * minutes; Kevin's ruling from the 5-7 Sep weekend is that the day-of data
 * renders the same at every age. A fifteen-minute-old strip is still the last
 * thing FencingTime Live posted, and it is read at arm's length in a sports
 * hall — the treatment that says "trust this less" is also the treatment that
 * makes it harder to read, on the one number the screen exists for.
 *
 * The age still reaches the reader, in WORDS: the amber "strip seen 14 min ago"
 * row below (446) and the amber "as of" (322, 327). §6 requires that colour
 * never be the only channel carrying staleness; with the fade gone, those words
 * are not the second channel any more, they are the whole of it, which is the
 * stronger half of the pair it always asked for.
 *
 * Full weight is the rule for every strip, so the component no longer needs to
 * be told the tier.
 *
 * IT CARRIES A CAPTION, and the caption is why: at 26px beside a "Pool 3" pill,
 * "B5" read as the second half of one phrase — "Pool 3 B5" — so the number was
 * large and its MEANING was the thing missing. §9 asks for the strip to be the
 * hero datum, which it still is; naming it costs one line of the smallest type
 * and is the difference between a number and a fact.
 *
 * Above rather than beside, because beside puts a word in the horizontal budget
 * the number needs on a phone. And rendered only WITH a value, so an absent
 * strip is still nothing at all rather than a label over an empty space.
 */
/** Which P's sentence a click or tap has opened on a row. */
type PriorityNote = { key: string; text: string; at: "name" | "matchup" };

type PriorityNoteState = {
  note: PriorityNote | null;
  setNote: (update: (n: PriorityNote | null) => PriorityNote | null) => void;
  ids: { name: string; matchup: string };
};

/**
 * THE P MARKER (post-Remenyik item 6): a small bold golden superscript "P"
 * after the name of whichever fencer in a DE matchup wins a tied bout under a
 * P-black card -- the higher initial seed. `--tc-amber`'s only new use, by
 * Kevin's choice.
 *
 * HOVER IS A `title`; CLICK AND TAP REVEAL THE LINE (Kevin's ruling, chosen
 * over a popover and over a legend). The sentence is real DOM text revealed in
 * place below the line on click or tap, which is what reaches a touch screen;
 * a mouse gets the native tooltip on hover and no line. This departs from
 * §4's "no title-attribute tooltips" for this one control, deliberately: the
 * tooltip is an extra for a pointer, never the only route, since the click
 * reveal is always there. A real <button> with aria-expanded; its accessible
 * name is the sentence itself, so a screen reader hears what the letter means
 * without opening it.
 */
function PriorityMark({
  name,
  at,
  state,
}: {
  name: string;
  at: "name" | "matchup";
  state: PriorityNoteState;
}) {
  const { t } = useTranslation();
  const text = t("active.de.priority", { name });
  const key = `${at}:${name}`;
  const open = state.note?.key === key;
  return (
    <button
      type="button"
      className={styles.priority}
      aria-expanded={open}
      aria-controls={at === "name" ? state.ids.name : state.ids.matchup}
      aria-label={text}
      title={text}
      onClick={() => state.setNote((n) => (n?.key === key ? null : { key, text, at }))}
    >
      <span aria-hidden="true">P</span>
    </button>
  );
}

/** A name, followed by the P when it holds priority. */
function Named({
  name,
  priority,
  state,
}: {
  name: string;
  priority: boolean;
  state: PriorityNoteState;
}) {
  return (
    <>
      {name}
      {priority ? <PriorityMark name={name} at="matchup" state={state} /> : null}
    </>
  );
}

/** The revealed sentence, under the line its P sits on. */
function PriorityNoteLine({ state, at }: { state: PriorityNoteState; at: "name" | "matchup" }) {
  if (state.note?.at !== at) return null;
  return (
    <div
      id={at === "name" ? state.ids.name : state.ids.matchup}
      className={styles.priorityNote}
      role="note"
    >
      {state.note.text}
    </div>
  );
}

/*
 * ELEMENTS INSIDE A TRANSLATED SENTENCE, without changing the catalogue. The
 * sentence is rendered with a marker in place of each name -- an invisible
 * separator around the placeholder's own key -- then split on the markers and
 * the names put back as elements, so the P can follow a name wherever a locale
 * puts it ("vs A", "A に対して") and no translator has to learn a tag.
 */
const MARK = "⁣";
function withNodes(sentence: string, nodes: Record<string, ReactNode>): ReactNode[] {
  return sentence
    .split(new RegExp(`${MARK}(\\w+)${MARK}`))
    .map((part, i) => (i % 2 === 1 ? <Fragment key={i}>{nodes[part]}</Fragment> : part));
}
const marker = (key: string) => `${MARK}${key}${MARK}`;

function Strip({ value }: { value: string | null | undefined }) {
  const { t } = useTranslation();

  // Null renders NOTHING — no dash, no "TBD", no empty box (contract §0). A
  // registered fencer with no assignment yet simply has no strip.
  if (!value) return null;

  return (
    <span className={styles.stripWrap}>
      <span className={styles.stripLabel}>{t("active.stripLabel")}</span>
      {/* No inline style at all: the size, weight and colour are the class's,
          and there is no longer any per-render variation to express here. */}
      <span className={`tc-num ${styles.strip}`}>{value}</span>
    </span>
  );
}

/**
 * The terminal state (§9): "empty and should feel like the day ending, stated
 * plainly, never like an error".
 *
 * So: no icon, no illustration, no "Oops", no retry button. Two different
 * sentences for two different days — see emptyReason — and a THIRD when the
 * reason this screen is empty is the scope it is reading (decision 503).
 *
 * THE SCOPE-EMPTY STATE IS 326'S ONE EXCEPTION, and this is where it is spent:
 * an empty page loses its controls, because a control over a list that is not
 * there reads as the app being broken. Here the control is the REMEDY for the
 * state, so hiding it would leave a reader told what is wrong and holding
 * nothing to fix it with.
 *
 * THE CONTROL AND THE SENTENCE ARE TWO QUESTIONS (521, Kevin's fix, correcting
 * this component). The SENTENCE keeps all three conditions below, because what
 * the copy may CLAIM is a different matter from what the reader may DO.
 *
 * ON ACTIVE THE CONTROL RENDERS WHENEVER THE ACCOUNT HOLDS MORE THAN ONE LIST
 * (decision 540, amending 326/521 here). 521 fixed the no-exit state for narrow
 * scopes and left its mirror image: widening to every list on an empty page
 * removed the only writer of a scope that lives on the user row and never in
 * the URL (501), so the reader could not narrow it again. 326's test is whether
 * the control is the REMEDY for the state, and over more than one list it
 * always is, in BOTH directions.
 *
 * No condition is needed here because `listScope` is already null unless the
 * account is granted multi-list, holds more than one list, and the scope has
 * arrived (510) — the route builds the node, this decides nothing. Upcoming and
 * Past keep 521's narrower rule; the divergence is deliberate and is Active's
 * alone, because Active is the view whose tab can disappear underneath a reader.
 *
 * ALL THREE CONDITIONS FOR THE SENTENCE, and the third is the one that is easy
 * to get wrong.
 * `hiddenLiveEventCount` already carries "the scope is narrower than all lists"
 * and "the count is not zero" from the route. `"everyone-done"` is excluded
 * here: that count spans every list (503), so on the day a reader has just
 * watched their own fencer finish it would include that event and the sentence
 * would claim it is somewhere else. `"nothing-live"` is the reason that cannot
 * be about this view's own lists — nobody on them is entered in anything live —
 * so every counted event really is on a list this view is not showing.
 */
/**
 * The top of the screen: the page's heading, for assistive technology only,
 * and the list-scope control where there is one.
 *
 * THE VISIBLE "ACTIVE NOW" EYEBROW IS GONE (decision 985, Kevin: it "has no
 * meaning or value"). The tab already says where the reader is, and the
 * tournament count it carried is the number of cards beneath it. The h1
 * stays, hidden, so the page still has the heading a screen reader jumps to,
 * named as the tab is. The row renders only around the control, so a screen
 * with none starts with its first card.
 */
function ActiveHead({ listScope }: { listScope: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <h1 className="tc-sr-only">{t("nav.active")}</h1>
      {listScope ? <div className={styles.head}>{listScope}</div> : null}
    </>
  );
}

function ActiveEmpty({
  reason,
  listScope = null,
  hiddenLiveEventCount,
}: {
  reason: "everyone-done" | "nothing-live";
  listScope?: ReactNode;
  hiddenLiveEventCount?: number;
}) {
  const { t } = useTranslation();

  const elsewhere =
    reason === "nothing-live" && hiddenLiveEventCount !== undefined && hiddenLiveEventCount > 0;

  return (
    <div className={styles.screen}>
      <ActiveHead listScope={listScope} />
      <p className={styles.empty}>
        {elsewhere
          ? // The number as it arrived, with no arithmetic: under the three
            // conditions above it is exactly the count of live events on lists
            // this view is not showing, and subtracting anything would be an
            // inference (decision 503).
            t("active.emptyElsewhere", { count: hiddenLiveEventCount })
          : reason === "everyone-done"
            ? t("active.emptyDone")
            : t("active.emptyNothingLive")}
      </p>
    </div>
  );
}
